import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import loadable from '@loadable/component';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayDeliveryPickup,
  displayDeliveryShipping,
  displayPrice,
  isPriceVariationsEnabled,
} from '../../util/configHelpers';
import {
  propTypes,
  AVAILABILITY_MULTIPLE_SEATS,
  LISTING_STATE_CLOSED,
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_FIXED,
  LINE_ITEM_ITEM,
  STOCK_MULTIPLE_ITEMS,
  STOCK_INFINITE_MULTIPLE_ITEMS,
  LISTING_STATE_PUBLISHED,
} from '../../util/types';
import { convertMoneyToNumber, formatMoney } from '../../util/currency';
import { createSlug, parse, stringify } from '../../util/urlHelpers';
import { userDisplayNameAsString } from '../../util/data';
import {
  INQUIRY_PROCESS_NAME,
  getSupportedProcessesInfo,
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';
import {
  AR_TEACHING_CATEGORY_LABELS,
  normalizeCategoryLookupKey,
  humanizeCategoryValue,
} from '../../util/helpersFunction';

import { ModalInMobile, PrimaryButton, AvatarSmall, H1, H2, IconReviewStar, ResponsiveImage, AspectRatioWrapper, LearningPlacementModal } from '../../components';
import PriceVariantPicker from './PriceVariantPicker/PriceVariantPicker';

import css from './OrderPanel.module.css';
import { getExperienceParts, getYearsFromListing } from '../../util/constants';
import IconCard from '../SavedCardDetails/IconCard/IconCard';
import { matchPathname } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { convertCurrency, convertUSDToSelected, getCurrencySymbol } from '../../util/currencyConversion';
import { fetchFxRates, getActiveLessonPack, getAllLearningProfilesForParent, familyChildren } from '../../util/api';

const BookingTimeForm = loadable(() =>
  import(/* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm')
);
const BookingDatesForm = loadable(() =>
  import(/* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm')
);
const BookingFixedDurationForm = loadable(() =>
  import(
    /* webpackChunkName: "BookingFixedDurationForm" */ './BookingFixedDurationForm/BookingFixedDurationForm'
  )
);
const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(/* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm')
);

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const TODAY = new Date();

const isPublishedListing = listing => {
  return listing.attributes.state === LISTING_STATE_PUBLISHED;
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};


const getCheapestPriceVariant = (priceVariants = []) => {
  return priceVariants.reduce((cheapest, current) => {
    return current.priceInSubunits < cheapest.priceInSubunits ? current : cheapest;
  }, priceVariants[0]);
};

const formatMoneyIfSupportedCurrency = (price, intl) => {
  try {
    return formatMoney(intl, price);
  } catch (e) {
    return `(${price.currency})`;
  }
};

const buildCategoryNameMap = categories => {
  const map = {};

  const walk = items => {
    if (!Array.isArray(items)) {
      return;
    }

    items.forEach(item => {
      if (!item) {
        return;
      }

      const id = item.id;
      const name = item.name || item.label || id;
      if (id && !map[id]) {
        map[id] = name;
      }
      walk(item.subcategories);
    });
  };

  walk(categories);
  return map;
};

const localizeCategoryLabel = (label, value, locale) => {
  const isArabicLocale = String(locale || '').toLowerCase().startsWith('ar');
  if (!isArabicLocale) {
    return label;
  }

  const fromValue = AR_TEACHING_CATEGORY_LABELS[normalizeCategoryLookupKey(value)];
  if (fromValue) {
    return fromValue;
  }

  const fromLabel = AR_TEACHING_CATEGORY_LABELS[normalizeCategoryLookupKey(label)];
  return fromLabel || label;
};

const normalizeCategoryValues = raw => {
  if (Array.isArray(raw)) {
    return raw
      .map(item => {
        if (typeof item === 'string') return item;
        if (typeof item === 'number') return String(item);
        if (item && typeof item === 'object') {
          return item.option || item.value || item.key || item.id || item.label || item.name || null;
        }
        return null;
      })
      .filter(Boolean);
  }

  if (typeof raw === 'string') {
    return raw.includes(',') ? raw.split(',').map(v => v.trim()).filter(Boolean) : [raw];
  }

  if (raw && typeof raw === 'object') {
    const candidate = raw.option || raw.value || raw.key || raw.id || raw.label || raw.name;
    return candidate ? [candidate] : [];
  }

  return [];
};

const pickTeachingCategoriesFromPublicData = publicData => {
  if (!publicData || typeof publicData !== 'object') {
    return [];
  }

  const directKeys = [
    'teaching_categories',
    'teachingCategories',
    'teachingcategories',
    'teaching_category',
    'teachingCategory',
  ];

  const directValues = directKeys.flatMap(key => normalizeCategoryValues(publicData[key]));
  if (directValues.length > 0) {
    return directValues;
  }

  // Fallback: detect custom extended-data key containing both "teaching" and "categor"
  const fromHeuristicKey = Object.entries(publicData)
    .filter(([key]) => {
      const normalized = String(key).toLowerCase().replace(/[_-\s]/g, '');
      return normalized.includes('teaching') && normalized.includes('categor');
    })
    .flatMap(([, value]) => normalizeCategoryValues(value));

  return fromHeuristicKey;
};

const getTeachingCategoryOptions = (publicData, categoryConfiguration, locale) => {
  const values = pickTeachingCategoriesFromPublicData(publicData);

  if (!values.length) {
    return [];
  }

  const uniqueValues = [...new Set(values.filter(Boolean))];
  const categoryNameMap = buildCategoryNameMap(categoryConfiguration?.categories || []);

  return uniqueValues.map(value => ({
    value,
    label: localizeCategoryLabel(categoryNameMap[value] || humanizeCategoryValue(value), value, locale),
  }));
};

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const { orderOpen, ...searchParams } = parse(search);
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const handleSubmit = (
  isOwnListing,
  isClosed,
  isInquiryWithoutPayment,
  onSubmit,
  history,
  location
) => {
  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.
  return isInquiryWithoutPayment
    ? () => onSubmit({})
    : () => openOrderModal(isOwnListing, isClosed, history, location);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const PriceMaybe = props => {
  const { price, publicData, config, intl, listingTypeConfig, showTrialBookingPrice, trialLessonPrice } = props;

  const selectedCurrency = useSelector(state => state.currency.selectedCurrency);
  const fxRates = useSelector(state => state.currency.fxRates);

  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  // Convert listing native price (EUR/USD/AED) to selected user currency
  const basePrice = price ? convertMoneyToNumber(price) : null;
  if (basePrice == null) return null;

  const conv = convertCurrency(basePrice, price?.currency || 'USD', selectedCurrency, fxRates);
  // Always round up any fractional value to the upper whole number (e.g. 183.01 -> 184)
  const priceAmountText = Math.ceil(conv.amount).toString();

  const displaySymbol = getCurrencySymbol(selectedCurrency).trim();

  return (
    <div className={css.price}>
      {hasMultiplePriceVariants ? (
        <span className={css.statLabel}>
          <FormattedMessage id="OrderPanel.startingFrom" defaultMessage="Starting from" />{' '}
          {displaySymbol} {priceAmountText}
        </span>
      ) : (
        <span className={css.statLabel}>
          {displaySymbol} {priceAmountText}
        </span>
      )}
    </div>
  );
};

const PriceMissing = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingPriceMissing" />
    </p>
  );
};
const InvalidCurrency = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
    </p>
  );
};

const InvalidPriceVariants = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingPriceVariantsAreInvalid" />
    </p>
  );
};

const hasUniqueVariants = priceVariants => {
  const priceVariantsSlugs = priceVariants?.map(variant =>
    variant.name ? createSlug(variant.name) : 'no-name'
  );
  return new Set(priceVariantsSlugs).size === priceVariants.length;
};

const hasValidPriceVariants = priceVariants => {
  const isArray = Array.isArray(priceVariants);
  const hasItems = isArray && priceVariants.length > 0;
  const variantsHaveNames = hasItems && priceVariants.every(variant => variant.name);
  const namesAreUnique = hasItems && hasUniqueVariants(priceVariants);

  return variantsHaveNames && namesAreUnique;
};

/**
 * @typedef {Object} ListingTypeConfig
 * @property {string} listingType - The type of the listing
 * @property {string} transactionType - The type of the transaction
 * @property {string} transactionType.process - The process descriptionof the transaction
 * @property {string} transactionType.alias - The alias of the transaction process
 * @property {string} transactionType.unitType - The unit type of the transaction
 */

/**
 * OrderPanel is a component that renders a panel for making bookings, purchases, or inquiries for a listing.
 * It handles different transaction processes and displays appropriate forms based on the listing type.
 *
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overwrites the default class for the root element
 * @param {string} [props.className] - Custom class that extends
 * @param {string} [props.titleClassName] - Custom class name for the title
 * @param {propTypes.listing} props.listing - The listing data (either regular or own listing)
 * @param {Array<ListingTypeConfig>} props.validListingTypes - Array of valid listing type configurations
 * @param {boolean} [props.isOwnListing=false] - Whether the listing belongs to the current user
 * @param {listingType.user|listingType.currentUser} props.author - The listing author's user data
 * @param {ReactNode} [props.authorLink] - Custom component for rendering the author link
 * @param {ReactNode} [props.payoutDetailsWarning] - Warning message about payout details
 * @param {Function} props.onSubmit - Handler for form submission
 * @param {ReactNode|string} props.title - Title of the panel
 * @param {ReactNode} [props.titleDesktop] - Alternative title for desktop view
 * @param {ReactNode|string} [props.subTitle] - Subtitle text
 * @param {Function} props.onManageDisableScrolling - Handler for managing scroll behavior
 * @param {Function} props.onFetchTimeSlots - Handler for fetching available time slots
 * @param {Object} [props.monthlyTimeSlots] - Available time slots by month
 * @param {Function} props.onFetchTransactionLineItems - Handler for fetching transaction line items
 * @param {Function} [props.onContactUser] - Handler for contacting the listing author
 * @param {Array} [props.lineItems] - Array of line items for the transaction
 * @param {boolean} props.fetchLineItemsInProgress - Whether line items are being fetched
 * @param {Object} [props.fetchLineItemsError] - Error object if line items fetch failed
 * @param {string} props.marketplaceCurrency - The currency used in the marketplace
 * @param {number} props.dayCountAvailableForBooking - Number of days available for booking
 * @param {string} props.marketplaceName - Name of the marketplace
 *
 * @returns {JSX.Element} Component that displays the order panel with appropriate form
 */
const OrderPanel = props => {
  const [mounted, setMounted] = useState(false);
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();
  const [showTrialBookingPrice, setTrialBooking] = useState(false);
  const routeConfiguration = useRouteConfiguration();
  useEffect(() => {
    setMounted(true);
  }, []);
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    title,
    titleDesktop,
    author,
    authorLink,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    timeSlotsForDate,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    showListingImage,
    // Inquiry props (bubble down to BookingFixedDurationForm)
    sendInquiryError,
    sendInquiryInProgress,
    onSubmitInquiry,
    reviews,
    trialLessonPrice,
    trialLesson20Min,
    isTrialEligible,
    avatarUrl,
    setInsufficientFundsOpen,
    insufficientFundsOpen,
    categoryConfiguration,
    inquiryModalOpen,
    onCloseInquiryModal,
  } = props;

  const currentPage = getResolvedCurrentPage(location, routeConfiguration);
  const publicData = listing?.attributes?.publicData || {};
  const { listingType, unitType, transactionProcessAlias = '', priceVariants, startTimeInterval } =
    publicData || {};
  const teachingCategoryOptions = getTeachingCategoryOptions(
    publicData,
    categoryConfiguration,
    intl?.locale
  );

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  const price = listing?.attributes?.price;
  const isPaymentProcess = processName !== INQUIRY_PROCESS_NAME;

  // Reviews: compute average rating and total count (public, not deleted)
  const reviewList = Array.isArray(reviews) ? reviews : [];
  const validReviews = reviewList.filter(
    r => r?.attributes?.state === 'public' && !r?.attributes?.deleted && typeof r?.attributes?.rating === 'number'
  );
  const reviewCount = validReviews.length;
  const averageRating = reviewCount
    ? validReviews.reduce((sum, r) => sum + (r.attributes.rating || 0), 0) / reviewCount
    : 0;

  const showPriceMissing = isPaymentProcess && !price;
  const showInvalidCurrency = isPaymentProcess && price?.currency !== marketplaceCurrency;

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;

  const isBooking = isBookingProcess(processName);
  const shouldHaveFixedBookingDuration = isBooking && [LINE_ITEM_FIXED].includes(lineItemUnitType);
  const showBookingFixedDurationForm =
    mounted && shouldHaveFixedBookingDuration && !isClosed && timeZone && priceVariants?.length > 0;

  const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  const showBookingTimeForm = mounted && shouldHaveBookingTime && !isClosed && timeZone;

  const shouldHaveBookingDates =
    isBooking && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const showBookingDatesForm = mounted && shouldHaveBookingDates && !isClosed && timeZone;

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const isPurchase = isPurchaseProcess(processName);
  const shouldHavePurchase = isPurchase && lineItemUnitType === LINE_ITEM_ITEM;
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock = shouldHavePurchase && !isClosed && currentStock === 0;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showProductOrderForm =
    mounted && shouldHavePurchase && !isClosed && typeof currentStock === 'number';

  const showInquiryForm = mounted && !isClosed && processName === INQUIRY_PROCESS_NAME;

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const { pickupEnabled, shippingEnabled, videoIntroduction, levels = [], sessionTimeMinutes = 50,
  } =
    listing?.attributes?.publicData || {};

  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  const displayPickup = displayDeliveryPickup(listingTypeConfig);
  const allowOrdersOfMultipleItems = [STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS].includes(
    listingTypeConfig?.stockType
  );

  const searchParams = parse(location.search);
  const isOrderOpen = !!searchParams.orderOpen;

  const currentUser = useSelector(state => state.user.currentUser);
  const isTutor = currentUser?.attributes?.profile?.publicData?.userType === 'tutor';
  const showLessonPacks = !isOwnListing && !isTutor && isPublishedListing(listing) && currentPage !== 'OrderDetailsPage';

  const experienceYears = getYearsFromListing(listing, author || listing?.author || currentUser);
  const experienceParts = useMemo(() => {
    return getExperienceParts(experienceYears, intl);
  }, [experienceYears, intl]);

  const [children, setChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState('');
  
  const isParent = currentUser?.attributes?.profile?.publicData?.isFamilyParent === true;
  const isFamilyChild = currentUser?.attributes?.profile?.publicData?.isFamilyChild === true;
  const isIndependentBookingEnabled = currentUser?.attributes?.profile?.protectedData?.independentBookingEnabled === true;
  const isBookingDisabledForChild = isFamilyChild && !isIndependentBookingEnabled;

  // Use stable primitive IDs — not the currentUser object — to avoid re-running on every Redux update.
  const currentUserIdForPanel = currentUser?.id?.uuid;

  const [completedProfileChildIds, setCompletedProfileChildIds] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [isPlacementModalOpen, setIsPlacementModalOpen] = useState(false);

  useEffect(() => {
    if (isParent && currentUserIdForPanel) {
      familyChildren({ parentUserId: currentUserIdForPanel })
        .then(res => {
          if (res?.success && Array.isArray(res.data)) {
            const dependentKids = res.data.filter(
              c => c.status === 'active' && c.independentBookingEnabled !== true
            );
            setChildren(dependentKids);
          }
        })
        .catch(e => console.warn('OrderPanel: failed to query family children:', e));
    }
  }, [isParent, currentUserIdForPanel]);

  const fetchProfiles = useCallback(async () => {
    if (isParent && currentUserIdForPanel) {
      setLoadingProfiles(true);
      try {
        const res = await getAllLearningProfilesForParent({ parentId: currentUserIdForPanel });
        const profilesList = res?.data?.data || res?.data || [];
        if (Array.isArray(profilesList)) {
          const ids = profilesList
            .map(p => p.childId)
            .filter(Boolean)
            .map(String);
          setCompletedProfileChildIds(ids);
        }
      } catch (e) {
        console.warn('OrderPanel: failed to query learning profiles:', e);
      } finally {
        setLoadingProfiles(false);
      }
    }
  }, [isParent, currentUserIdForPanel]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const selectedChild = children.find(c => String(c.childUserId) === String(selectedChildId));
  const isChildQuestionnaireMissing =
    isParent &&
    !!selectedChildId &&
    !loadingProfiles &&
    !completedProfileChildIds.includes(String(selectedChildId));

  const handleWrappedSubmit = (formValues) => {
    const nextValues = { ...formValues };
    if (isParent && selectedChildId) {
      nextValues.childUserId = selectedChildId;
    }
    onSubmit(nextValues);
  };
  
  const [activePackState, setActivePackState] = useState({ studentId: '', pack: null });
  const activePack = activePackState.pack;
  const tutorIdForOrderPanel = listing?.author?.id?.uuid;
  useEffect(() => {
    const checkPack = async () => {
      const studentId = (isParent && selectedChildId) ? selectedChildId : currentUserIdForPanel;
      const tutorId = tutorIdForOrderPanel;
      if (studentId && tutorId) {
        try {
          const res = await getActiveLessonPack({ studentId, tutorId });
          if (res?.success && res?.data) {
            setActivePackState({ studentId, pack: res.data });
          } else {
            setActivePackState({ studentId, pack: null });
          }
        } catch (e) {
          console.warn('Failed to fetch active lesson pack:', e);
          setActivePackState({ studentId, pack: null });
        }
      } else {
        setActivePackState({ studentId: '', pack: null });
      }
    };
    checkPack();
  // Use stable primitive IDs — not objects (currentUser, listing) — as deps.
  }, [currentUserIdForPanel, tutorIdForOrderPanel, selectedChildId, isParent]);

  // Keep a live ref to the most recent BookingTimeForm values so we can
  // re-fire the line-items request when child selection or pack changes
  // without requiring the user to re-pick a time slot.
  const latestBookingValuesRef = useRef({});
  const onBookingValuesChange = useCallback((values) => {
    latestBookingValuesRef.current = values || {};
  }, []);

  // When the selected child or the active pack changes, immediately re-fetch
  // line items using the already-selected booking times (if any).
  const activePackId = activePack?.packId || null;
  const childUserIdForLineItems = (isParent && selectedChildId) ? selectedChildId : null;
  const lastFetchedStudentIdRef = useRef(null);
  const lastFetchedPackIdRef = useRef(null);

  useEffect(() => {
    const currentStudentId = (isParent && selectedChildId) ? selectedChildId : currentUserIdForPanel;
    console.log('OrderPanel price fetch check:', {
      currentStudentId,
      activePackStudentId: activePackState.studentId,
      match: activePackState.studentId === currentStudentId,
      selectedChildId,
      currentUserIdForPanel,
    });
    // Prevent fetching line items if the activePack state is stale (doesn't match the selected student/child yet)
    if (activePackState.studentId !== currentStudentId) {
      console.log('OrderPanel Price Fetch skipped: studentId mismatch');
      return;
    }

    const { bookingStartTime, bookingEndTime, startDate: rawStartDate, endDate: rawEndDate } = latestBookingValuesRef.current;
    const startTime = bookingStartTime || (rawStartDate instanceof Date ? rawStartDate.getTime().toString() : rawStartDate);
    const endTime = bookingEndTime || (rawEndDate instanceof Date ? rawEndDate.getTime().toString() : rawEndDate);

    if (!startTime || !endTime || startTime >= endTime) {
      console.log('OrderPanel Price Fetch skipped: missing or invalid booking times');
      return;
    }

    // Force fetch if the selected student/child or the active pack has changed,
    // bypassing the in-progress guard to avoid stale pricing displays.
    const isContextChanged =
      lastFetchedStudentIdRef.current !== currentStudentId ||
      lastFetchedPackIdRef.current !== activePackId;

    console.log('OrderPanel context change check:', {
      isContextChanged,
      lastFetchedStudentId: lastFetchedStudentIdRef.current,
      currentStudentId,
      lastFetchedPackId: lastFetchedPackIdRef.current,
      activePackId,
      fetchLineItemsInProgress,
    });

    if (!listing?.id || (fetchLineItemsInProgress && !isContextChanged)) {
      console.log('OrderPanel Price Fetch skipped: in progress or no listing id');
      return;
    }

    lastFetchedStudentIdRef.current = currentStudentId;
    lastFetchedPackIdRef.current = activePackId;

    const startDate = new Date(parseInt(startTime, 10));
    const endDate = new Date(parseInt(endTime, 10));
    const priceVariantName = latestBookingValuesRef.current?.priceVariantName;

    const orderData = {
      bookingStart: startDate,
      bookingEnd: endDate,
      ...(priceVariantName ? { priceVariantName } : {}),
      ...(activePackId ? { packId: activePackId } : {}),
      ...(childUserIdForLineItems ? { childUserId: childUserIdForLineItems } : {}),
    };

    console.log('OrderPanel calling onFetchTransactionLineItems with:', orderData);

    onFetchTransactionLineItems({
      orderData,
      listingId: listing.id,
      isOwnListing,
    });
  // Re-run whenever the child or pack changes — not on every render.
  }, [selectedChildId, activePackState]);
  const preselectedPriceVariantSlug = searchParams.bookableOption;
  const openInquiryOnLoad = String(searchParams.contactOpen) === 'true';

  const seatsEnabled = [AVAILABILITY_MULTIPLE_SEATS].includes(listingTypeConfig?.availabilityType);

  // Note: publicData contains priceVariationsEnabled if listing is created with priceVariations enabled.
  const isPriceVariationsInUse = !!publicData?.priceVariationsEnabled;
  const preselectedPriceVariant =
    Array.isArray(priceVariants) && preselectedPriceVariantSlug && isPriceVariationsInUse
      ? priceVariants.find(pv => pv?.name && createSlug(pv?.name) === preselectedPriceVariantSlug)
      : null;

  const priceVariantsMaybe = isPriceVariationsInUse
    ? {
      isPriceVariationsInUse,
      priceVariants,
      priceVariantFieldComponent: PriceVariantPicker,
      preselectedPriceVariant,
      isPublishedListing: isPublishedListing(listing),
    }
    : !isPriceVariationsInUse && showBookingFixedDurationForm
      ? {
        isPriceVariationsInUse: false,
        priceVariants: [getCheapestPriceVariant(priceVariants)],
        priceVariantFieldComponent: PriceVariantPicker,
      }
      : {};

  const showInvalidPriceVariantsMessage =
    isPriceVariationsInUse && !hasValidPriceVariants(priceVariants);

  const sharedProps = {
    lineItemUnitType,
    onSubmit: handleWrappedSubmit,
    price,
    marketplaceCurrency,
    listingId: listing.id,
    isOwnListing,
    marketplaceName,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    trialLessonPrice,
    trialLesson20Min,
    isTrialEligible,
    routeConfiguration,
    history,
    // Pass the active pack ID and selected child so BookingTimeForm
    // can include them in orderData when fetching line items.
    // This ensures the server applies the correct lesson-pack discount
    // for the selected learner without a page refresh.
    activePack: activePack,
    activePackId: activePackId,
    childUserId: childUserIdForLineItems,
    // Callback so OrderPanel can track the latest booking times and
    // re-fire the line-items request when child/pack changes.
    onBookingValuesChange,
  };

  const showClosedListingHelpText = listing.id && isClosed;

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  const authorDisplayName = userDisplayNameAsString(author, '');
  const listingTitleString = listing?.attributes?.title || '';

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.orderTitle);

  // Video controls and overlay
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const overlayHideTimer = useRef(null);

  const clearOverlayTimer = () => {
    if (overlayHideTimer.current) {
      clearTimeout(overlayHideTimer.current);
      overlayHideTimer.current = null;
    }
  };

  const scheduleOverlayHide = (delayMs = 3000) => {
    clearOverlayTimer();
    overlayHideTimer.current = setTimeout(() => setShowOverlay(false), delayMs);
  };
  const handleOverlayToggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused || v.ended) {
      v.play();
    } else {
      v.pause();
    }
  };
  const onVideoPlay = () => {
    setIsPlaying(true);
    setShowOverlay(true);
    scheduleOverlayHide(5000);
  };
  const onVideoPause = () => {
    setIsPlaying(false);
    clearOverlayTimer();
    setShowOverlay(true);
  };
  const onVideoEnded = () => {
    setIsPlaying(false);
    clearOverlayTimer();
    setShowOverlay(true);
  };

  useEffect(() => () => clearOverlayTimer(), []);
  const image = listing?.author?.profileImage;
  const imageVariants = image?.attributes?.variants || [];
  return (
    <div className={classes}>
      <ModalInMobile
        containerClassName={css.modalContainer}
        id="OrderFormInModal"
        isModalOpenOnMobile={isOrderOpen}
        onClose={() => {
          closeOrderModal(history, location);
          // Defer scroll until after modal has toggled scrolling back on
          setTimeout(() => {
            if (typeof window !== 'undefined') {
              window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
              if (document?.documentElement) document.documentElement.scrollTop = 0;
              if (document?.body) document.body.scrollTop = 0;
            }
          }, 0);
        }}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        {/* <div className={css.modalHeading}>
          <H1 className={css.heading}>{title}</H1>
        </div> */}

        {/* {showListingImage && (
          <div className={css.orderHeading}>
            {titleDesktop ? titleDesktop : <H2 className={titleClasses}>{title}</H2>}
            {subTitleText ? <div className={css.orderHelp}>{subTitleText}</div> : null}
          </div>
        )} */}
        {videoIntroduction && currentPage !== 'OrderDetailsPage' ? (
          <div
            className={css.videoContainer}
            onMouseEnter={() => {
              setShowOverlay(true);
              clearOverlayTimer();
            }}
            onMouseLeave={() => {
              if (isPlaying) scheduleOverlayHide(1500);
            }}
          >
            <video
              ref={videoRef}
              className={css.videoElement}
              src={videoIntroduction}
              controls={false}
              controlsList="nodownload noplaybackrate noremoteplayback"
              disablePictureInPicture
              disableRemotePlayback
              onContextMenu={(e) => e.preventDefault()}
              onPlay={onVideoPlay}
              onPause={onVideoPause}
              onEnded={onVideoEnded}
            />
            {showOverlay && (
              <button
                type="button"
                className={css.playOverlay}
                aria-label={isPlaying ? 'Pause video' : 'Play video'}
                onClick={handleOverlayToggle}
              >
                {isPlaying ? (
                  <svg className={css.iconSvg} viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="5" y="4" width="5" height="16" fill="currentColor" rx="1" />
                    <rect x="14" y="4" width="5" height="16" fill="currentColor" rx="1" />
                  </svg>
                ) : (
                  <svg className={css.iconSvg} viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M8 5v14l11-7z" fill="currentColor" />
                  </svg>
                )}
              </button>
            )}
          </div>
        ) : avatarUrl ? (
          <div className={css.noVideo}>
            <AspectRatioWrapper width={1} height={1}>
              <ResponsiveImage
                rootClassName={css.rootForImage}
                image={image}
                variants={['listing-card']}
              />
            </AspectRatioWrapper>
          </div>
        ) : null
        }
        <div className={css.orderWrapper}>
          <div className={classNames(css.orderHead, { [css.hasTwoStats]: reviewCount > 0, [css.hasSingleStat]: !reviewCount || reviewCount <= 0 })}>
            {/* Level column removed per Item 2.1 scope specifications */}
            <div className={css.orderCol}>
              <PriceMaybe
                price={price}
                publicData={publicData}
                validListingTypes={validListingTypes}
                intl={intl}
                marketplaceCurrency={marketplaceCurrency}
                showTrialBookingPrice={showTrialBookingPrice}
                trialLessonPrice={trialLessonPrice}
              />
              <div className={css.sessionLength}>
                <FormattedMessage
                  id="OrderPanel.sessionLength"
                  defaultMessage="{minutes}-min lesson"
                  values={{ minutes: showTrialBookingPrice ? 20 : sessionTimeMinutes }}
                />
              </div>
            </div>
            {reviewCount > 0 && (
              <div className={css.statBox}>
                <div className={css.statIconRow}>
                  <IconReviewStar isFilled className={css.star} label="rating" />
                  <span className={css.averageRating}>{averageRating.toFixed(1)}</span>
                </div>
                <div className={css.statValue}>
                  {intl.formatMessage(
                    { id: 'ListingCard.reviewsCount' },
                    { count: reviewCount }
                  )}
                </div>
              </div>
            )}
          </div>

          {isParent && children.length > 0 && (
            <div className={css.learnerDropdownWrapper}>
              <label htmlFor="learner-select" className={css.learnerLabel}>
                <FormattedMessage id="OrderPanel.selectLearnerLabel" defaultMessage="Select Learner Profile" />
              </label>
              <select
                id="learner-select"
                value={selectedChildId}
                onChange={(e) => setSelectedChildId(e.target.value)}
                className={css.learnerSelect}
              >
                <option value="">
                  {intl.formatMessage({ id: 'OrderPanel.myselfParent', defaultMessage: 'Myself (Parent)' })}
                </option>
                {children.map(child => {
                  const hasProfile = completedProfileChildIds.includes(String(child.childUserId));
                  const neededTag = !hasProfile
                    ? intl.formatMessage({ id: 'OrderPanel.questionnaireNeededOption', defaultMessage: ' (Questionnaire needed)' })
                    : '';
                  return (
                    <option key={child.id} value={child.childUserId}>
                      {child.childName}{neededTag}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {isBookingDisabledForChild ? (
            <div className={css.warningCard}>
              <div className={css.warningIcon}>🔒</div>
              <p className={css.warningText}>
                Your parent needs to book this lesson for you. Independent booking is currently disabled on your profile.
              </p>
            </div>
          ) : isChildQuestionnaireMissing ? (
            <div className={css.questionnaireGateCard}>
              <div className={css.gateIconBadge}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                  <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                  <path d="M9 14l2 2 4-4" />
                </svg>
              </div>
              <h4 className={css.gateTitle}>
                <FormattedMessage
                  id="OrderPanel.questionnaireRequiredTitle"
                  defaultMessage="Placement Questionnaire Required"
                />
              </h4>
              <p className={css.gateDescription}>
                <FormattedMessage
                  id="OrderPanel.questionnaireRequiredDescription"
                  defaultMessage="Before booking a lesson for {childName}, they must complete their Arabic placement questionnaire. This takes only 2 minutes and ensures the tutor knows where to start."
                  values={{ childName: selectedChild?.childName || 'this child' }}
                />
              </p>
              <button
                type="button"
                className={css.completeQuestionnaireBtn}
                onClick={() => setIsPlacementModalOpen(true)}
              >
                <FormattedMessage
                  id="OrderPanel.completeQuestionnaireBtn"
                  defaultMessage="Complete Questionnaire Now"
                />
              </button>
            </div>
          ) : showPriceMissing ? (
            <PriceMissing />
          ) : showInvalidCurrency ? (
            <InvalidCurrency />
          ) : showInvalidPriceVariantsMessage ? (
            <InvalidPriceVariants />
          ) : showBookingFixedDurationForm ? (
            <BookingFixedDurationForm
              seatsEnabled={seatsEnabled}
              className={css.bookingForm}
              formId="OrderPanelBookingFixedDurationForm"
              dayCountAvailableForBooking={dayCountAvailableForBooking}
              monthlyTimeSlots={monthlyTimeSlots}
              timeSlotsForDate={timeSlotsForDate}
              onFetchTimeSlots={onFetchTimeSlots}
              startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
              startTimeInterval={startTimeInterval}
              timeZone={timeZone}
              {...priceVariantsMaybe}
              {...sharedProps}
              // Inquiry modal props
              listingTitle={listingTitleString}
              authorDisplayName={authorDisplayName}
              onManageDisableScrolling={onManageDisableScrolling}
              sendInquiryError={sendInquiryError}
              sendInquiryInProgress={sendInquiryInProgress}
              onSubmitInquiry={onSubmitInquiry}
              openInquiryOnLoad={openInquiryOnLoad}
              setTrialBooking={setTrialBooking}
              showTrialBookingPrice={showTrialBookingPrice}
              listing={listing}
              insufficientFundsOpen={insufficientFundsOpen}
              setInsufficientFundsOpen={setInsufficientFundsOpen}
              teachingCategoryOptions={teachingCategoryOptions}
              inquiryModalOpen={inquiryModalOpen}
              onCloseInquiryModal={onCloseInquiryModal}
              activePack={activePack}
            />
          ) : showBookingTimeForm ? (
            <BookingTimeForm
              seatsEnabled={seatsEnabled}
              className={css.bookingForm}
              formId="OrderPanelBookingTimeForm"
              dayCountAvailableForBooking={dayCountAvailableForBooking}
              monthlyTimeSlots={monthlyTimeSlots}
              timeSlotsForDate={timeSlotsForDate}
              onFetchTimeSlots={onFetchTimeSlots}
              startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
              endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
              timeZone={timeZone}
              {...priceVariantsMaybe}
              {...sharedProps}
              teachingCategoryOptions={teachingCategoryOptions}
            />
          ) : showBookingDatesForm ? (
            <BookingDatesForm
              seatsEnabled={seatsEnabled}
              className={css.bookingForm}
              formId="OrderPanelBookingDatesForm"
              dayCountAvailableForBooking={dayCountAvailableForBooking}
              monthlyTimeSlots={monthlyTimeSlots}
              onFetchTimeSlots={onFetchTimeSlots}
              timeZone={timeZone}
              {...priceVariantsMaybe}
              {...sharedProps}
              teachingCategoryOptions={teachingCategoryOptions}
            />
          ) : showProductOrderForm ? (
            <ProductOrderForm
              formId="OrderPanelProductOrderForm"
              currentStock={currentStock}
              allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
              pickupEnabled={pickupEnabled && displayPickup}
              shippingEnabled={shippingEnabled && displayShipping}
              displayDeliveryMethod={displayPickup || displayShipping}
              onContactUser={onContactUser}
              {...sharedProps}
            />
          ) : showInquiryForm ? (
            <InquiryWithoutPaymentForm formId="OrderPanelInquiryForm" onSubmit={onSubmit} />
          ) : !isKnownProcess ? (
            <p className={css.errorSidebar}>
              <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
            </p>
          ) : null}

          {isParent && selectedChild && (
            <LearningPlacementModal
              id="OrderPanelPlacementModal"
              isOpen={isPlacementModalOpen}
              onClose={() => setIsPlacementModalOpen(false)}
              onManageDisableScrolling={onManageDisableScrolling}
              parentId={currentUserIdForPanel}
              childId={selectedChildId}
              learnerName={selectedChild.childName}
              onSuccess={() => {
                setIsPlacementModalOpen(false);
                setCompletedProfileChildIds(prev => [...prev, String(selectedChildId)]);
                fetchProfiles();
              }}
            />
          )}
        </div>
      </ModalInMobile>
      <div className={css.openOrderForm}>
        <PriceMaybe
          price={price}
          publicData={publicData}
          validListingTypes={validListingTypes}
          intl={intl}
          marketplaceCurrency={marketplaceCurrency}
          showCurrencyMismatch
        />

        {isClosed ? (
          <div className={css.closedListingButton}>
            <FormattedMessage id="OrderPanel.closedListingButtonText" />
          </div>
        ) : (
          <PrimaryButton
            className={css.primaryButtonMobile}
            onClick={handleSubmit(
              isOwnListing,
              isClosed,
              showInquiryForm,
              onSubmit,
              history,
              location
            )}
            disabled={isOutOfStock}
          >
            {isBooking ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageBooking" />
            ) : isOutOfStock ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageNoStock" />
            ) : isPurchase ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
            ) : (
              <FormattedMessage id="OrderPanel.ctaButtonMessageInquiry" />
            )}
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};

export default OrderPanel;
